import React from 'react';
import TabContext from '@mui/lab/TabContext';

import { useDispatch, useSelector } from 'react-redux';
import { TabList, TabPanel } from '@mui/lab';
import { Alert, Divider, Stack, Tab } from '@mui/material';
import { ArticleOutlined, SecurityOutlined, SettingsOutlined } from '@mui/icons-material';

import { NodeFieldType } from '../../types';
import { selectCommandByNodeId, showTelnyxFeatures, State, tabActions } from '@store/index';
import { After, ToggleJSON } from '@common/Node/NodeSidebar/Custom';
import { getModelKey, ModelAuthKey } from '@common/NodeAuth/ModelAuthKey';
import { Button } from '@common/Button';
import { Toggle } from '@common/Node/NodeSidebar/Custom/Toggle';

import { NodeInputExpression, NodeSidebar, NodeSidebarTab, NodeSidebarTabsContainer } from '@common/Node/NodeSidebar';
import { NodeExecuteData } from '@common/Node/NodeSidebar/NodeData';
import { useSecrets } from '@common/hooks/useSecrets';
import { isPaidModel } from '@common/Autocomplete/Base';

interface Props {
  tab: string;
  setTab: React.Dispatch<string>;
  dataLoading: boolean;
}

export const CompletionTabs: React.FC<Props> = ({ tab, setTab, dataLoading }) => {
  const dispatch = useDispatch();

  const { secrets, loading, refetch, error } = useSecrets();

  const selectedNode = useSelector((state: State) => state.tabs.active.selectedNode);
  const authentication = useSelector((state: State) => state.tabs.active.flow?.authentication);
  const trace = useSelector((state: State) => state.tabs.active.run);

  const themeType = useSelector((state: State) => state.flow.theme);
  const isTelnyxUser = useSelector((state: State) => showTelnyxFeatures(state));

  const command = useSelector((state: State) => selectCommandByNodeId(state, state.tabs.active.selectedNode));
  if (!command) return;

  const fields = command.fields;
  const state = command.state;

  const model = state.model || '';

  const modelKey = getModelKey(model);
  const secretName = modelKey ? authentication?.[modelKey] : null;
  const authRequired = (!secretName || !secrets?.find((s) => s.identifier === secretName)) && isPaidModel(model);

  const background = themeType === 'dark' ? '#7272fa' : command.config.background;

  const messageArrayField = fields.find((field) => field.id === 'messages');
  const nameField = fields.find((field) => field.id === 'name');
  const modelField = fields.find((field) => field.id === 'model');
  const queryField = fields.find((field) => field.id === 'query');
  const systemField = fields.find((field) => field.id === 'system');
  const sessionIdField = fields.find((field) => field.id === 'session_id');
  const userIdField = fields.find((field) => field.id === 'user_id');
  const messageStorageField = fields.find((field) => field.id === 'message_storage');

  const optionsField = fields.find((field) => field.type === NodeFieldType.ToggleJSON);
  const afterField = fields.find((field) => field.type === NodeFieldType.After);
  const lowLatencyField = fields.find((field) => field.id === 'low_latency_beta');

  const nodeErrors = trace.nodeErrors;
  const nodeError = nodeErrors.find((error) => error.node_id === selectedNode);
  const jsonModeEnabled = state?.json_mode?.enabled;
  const messageArrayEnabled = state?.message_array?.enabled && !jsonModeEnabled;

  const sessionIdFieldValue = state?.session_id?.value;
  const sessionIdValueExists = sessionIdFieldValue !== '';

  return (
    <NodeSidebar>
      {nodeError && (
        <Stack padding='16px 20px 0 20px'>
          <Alert
            severity='error'
            action={
              <Button
                customColor='error'
                small
                onClick={() => dispatch(tabActions.clearWorkflowNodeError(command.node.id))}
              >
                Clear
              </Button>
            }
          >
            {nodeError.error}
          </Alert>
        </Stack>
      )}
      <TabContext value={authRequired ? '4' : tab}>
        <NodeSidebarTabsContainer color={background}>
          <TabList onChange={(_, newTab) => setTab(newTab)}>
            <Tab disabled={authRequired} disableRipple label='Node' value='1' />
            <Tab
              disabled={authRequired}
              title='Data'
              disableRipple
              iconPosition='start'
              icon={<ArticleOutlined />}
              value='3'
            />
            <Tab
              title='Auth'
              disabled={!isPaidModel(model)}
              disableRipple
              iconPosition='start'
              icon={<SecurityOutlined />}
              value='4'
            />
            <Tab
              disabled={authRequired}
              title='Settings'
              disableRipple
              iconPosition='start'
              icon={<SettingsOutlined />}
              value='5'
            />
          </TabList>
        </NodeSidebarTabsContainer>
        <TabPanel value='1'>
          <NodeSidebarTab>
            {nameField && <NodeInputExpression field={nameField} command={command} />}
            {modelField && <NodeInputExpression field={modelField} command={command} />}
            {queryField && model && !messageArrayEnabled && (
              <NodeInputExpression field={queryField} command={command} />
            )}
            {systemField && !jsonModeEnabled && !messageArrayEnabled && model && (
              <NodeInputExpression field={systemField} command={command} />
            )}
          </NodeSidebarTab>
          {model && !messageArrayEnabled && <Divider />}
          {model && !messageArrayEnabled && (
            <NodeSidebarTab>
              {model && sessionIdField && (
                <NodeInputExpression key={sessionIdField.id} field={sessionIdField} command={command} />
              )}
              {isTelnyxUser && messageStorageField && sessionIdValueExists && (
                <NodeInputExpression field={messageStorageField} command={command} />
              )}
              {model && userIdField && !jsonModeEnabled && (
                <NodeInputExpression key={userIdField.id} field={userIdField} command={command} />
              )}
            </NodeSidebarTab>
          )}
          <Divider />
          {optionsField && !messageArrayEnabled && (
            <NodeSidebarTab>
              <ToggleJSON command={command} field={optionsField} index={0} />
            </NodeSidebarTab>
          )}
          {messageArrayEnabled && model && messageArrayField && (
            <NodeSidebarTab>
              <NodeInputExpression field={messageArrayField} command={command} />
            </NodeSidebarTab>
          )}
        </TabPanel>
        <TabPanel value='3'>
          <NodeExecuteData command={command} loading={dataLoading} />
        </TabPanel>
        <TabPanel value='4'>
          <NodeSidebarTab>
            <ModelAuthKey
              command={command}
              secrets={secrets}
              secretsLoading={loading}
              secretsError={error}
              refetch={refetch}
              setTab={setTab}
            />
          </NodeSidebarTab>
        </TabPanel>
        <TabPanel value='5'>
          {isTelnyxUser && lowLatencyField && (
            <>
              <NodeSidebarTab>
                <Toggle key={lowLatencyField.id} field={lowLatencyField} command={command} />
              </NodeSidebarTab>
              <Divider />
            </>
          )}
          {afterField && <After command={command} field={afterField} />}
          <Divider />
        </TabPanel>
      </TabContext>
    </NodeSidebar>
  );
};
